import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Copy, ExternalLink, Database } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { searchBotsByName, searchBotsByToken, BotSearchResult } from '@/services/bot-checker-service';
import { useDebounce } from '@/hooks/use-debounce';

const BotCheckerPage = () => {
  const [nameQuery, setNameQuery] = useState('');
  const [tokenQuery, setTokenQuery] = useState('');
  
  // Debounce search queries
  const debouncedNameQuery = useDebounce(nameQuery, 500);
  const debouncedTokenQuery = useDebounce(tokenQuery, 500);

  // Search by name query
  const { data: nameResults = [], isLoading: isLoadingName } = useQuery({
    queryKey: ['searchBotsByName', debouncedNameQuery],
    queryFn: () => searchBotsByName(debouncedNameQuery),
    enabled: debouncedNameQuery.length > 0,
  });

  // Search by token query
  const { data: tokenResults = [], isLoading: isLoadingToken } = useQuery({
    queryKey: ['searchBotsByToken', debouncedTokenQuery],
    queryFn: () => searchBotsByToken(debouncedTokenQuery),
    enabled: debouncedTokenQuery.length > 0,
  });

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} đã được sao chép!`);
    } catch (err) {
      toast.error('Không thể sao chép');
    }
  };

  const getBotTypeBadge = (type: string) => {
    const colors = {
      user_bot: 'bg-blue-500/20 text-blue-400',
      premium_bot: 'bg-purple-500/20 text-purple-400',
      prop_bot: 'bg-emerald-500/20 text-emerald-400',
      shadow_bot: 'bg-orange-500/20 text-orange-400'
    };
    
    return colors[type as keyof typeof colors] || 'bg-gray-500/20 text-gray-400';
  };

  const renderBotTable = (results: BotSearchResult[], title: string, isLoading: boolean) => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5" />
          {title}
        </CardTitle>
        <CardDescription>
          {results.length > 0 ? `Tìm thấy ${results.length} kết quả` : 'Không có kết quả'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
          </div>
        ) : results.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tên Bot</TableHead>
                  <TableHead>Signal Token</TableHead>
                  <TableHead>Reverse Token</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {results.map((bot) => (
                  <TableRow key={bot.id}>
                    <TableCell className="font-medium">
                      <div className="flex flex-col">
                        <span>{bot.name}</span>
                        <span className="text-xs text-muted-foreground">{bot.id}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <code className="text-xs bg-muted px-2 py-1 rounded">
                          {bot.signal_token}
                        </code>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copyToClipboard(bot.signal_token, 'Signal Token')}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      {bot.reverse_signal_token ? (
                        <div className="flex items-center gap-2">
                          <code className="text-xs bg-muted px-2 py-1 rounded">
                            {bot.reverse_signal_token}
                          </code>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => copyToClipboard(bot.reverse_signal_token!, 'Reverse Token')}
                          >
                            <Copy className="h-3 w-3" />
                          </Button>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={getBotTypeBadge(bot.type)}>
                        {bot.type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={bot.status === 'active' ? 'default' : 'secondary'}>
                        {bot.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => window.open(`/bots/${bot.id}`, '_blank')}
                      >
                        <ExternalLink className="h-3 w-3" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            Không tìm thấy bot nào
          </div>
        )}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold mb-2">Bot Checker</h1>
        <p className="text-muted-foreground">
          Công cụ kiểm tra và tìm kiếm thông tin bot theo tên hoặc signal token
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Search by Name */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Tìm theo tên Bot
            </CardTitle>
            <CardDescription>
              Nhập tên bot để tìm signal token
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Input
              placeholder="Nhập tên bot..."
              value={nameQuery}
              onChange={(e) => setNameQuery(e.target.value)}
              className="w-full"
            />
          </CardContent>
        </Card>

        {/* Search by Token */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Tìm theo Signal Token
            </CardTitle>
            <CardDescription>
              Nhập signal token để tìm thông tin bot
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Input
              placeholder="Nhập signal token..."
              value={tokenQuery}
              onChange={(e) => setTokenQuery(e.target.value)}
              className="w-full"
            />
          </CardContent>
        </Card>
      </div>

      <div className="space-y-6">
        {/* Name Search Results */}
        {debouncedNameQuery && renderBotTable(nameResults, 'Kết quả tìm theo tên', isLoadingName)}
        
        {/* Token Search Results */}
        {debouncedTokenQuery && renderBotTable(tokenResults, 'Kết quả tìm theo token', isLoadingToken)}
      </div>
    </div>
  );
};

export default BotCheckerPage;